import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { addItemToCart } from "@/lib/cart";
import { toast } from "sonner";
import { useState } from "react";
import { SEOHead } from "@/components/SEOHead";

// ... existing code ...
const ProductDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [qty, setQty] = useState(1);

  const { data: product } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("id, title, description, price_cents, currency, shop:shops(handle,name)")
        .eq("id", id)
        .maybeSingle();
      return data;
    },
    enabled: !!id,
  });

  const { data: imageUrl } = useQuery({
    queryKey: ["product-image", id],
    queryFn: async () => {
      if (!id) return "";
      const { data } = await supabase
        .from("product_images")
        .select("url, sort")
        .eq("product_id", id)
        .order("sort", { ascending: true })
        .limit(1);
      return data?.[0]?.url ?? "";
    },
    enabled: !!id,
  });

  // ... existing code ...
  const { data: related } = useQuery({
    queryKey: ["related", id],
    queryFn: async () => {
      if (!id) return [] as any[];
      // Find one category for this product
      const pc = await supabase
        .from("product_categories")
        .select("category_id")
        .eq("product_id", id)
        .limit(1);
      const catId = pc.data?.[0]?.category_id;
      if (!catId) return [] as any[];
      const { data } = await supabase
        .from("product_categories")
        .select("product:products(id, title, price_cents, currency, product_images(url, sort))")
        .eq("category_id", catId)
        .limit(12);
      return (data ?? [])
        .map((r: any) => r.product)
        .filter((p: any) => p && p.id !== id)
        .slice(0, 4);
    },
    enabled: !!id,
  });
  // ... existing code ...
  const addMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Please login to add to cart");
      if (!id) throw new Error("Missing product id");
      const n = Math.max(1, Math.floor(Number(qty)) || 1);
      await addItemToCart(user.id, id, n);
    },
    onSuccess: () => toast.success("Added to cart"),
    onError: (e: any) => toast.error(e.message || "Failed to add to cart"),
  });

  if (!product) return (
    <main className="container mx-auto px-4 py-10">
      <SEOHead
        title="Product Not Found"
        description="The requested product could not be found on BahaMarket."
      />
      <h1 className="text-3xl font-heading font-bold mb-6">Product</h1>
      <p className="text-muted-foreground">Product not found or loading...</p>
    </main>
  );
  return (
    <>
      <SEOHead
        title={product.title}
        description={product.description || `${product.title} available on BahaMarket from ${product.shop?.name || 'local vendor'}`}
        keywords={`${product.title}, Bahamas, ${product.shop?.name}, Caribbean marketplace`}
        ogImage={imageUrl || undefined}
      />
      <main className="container mx-auto px-4 py-10 grid gap-6 md:grid-cols-2">
        <div className="w-full">
          <div className="aspect-square rounded-xl border bg-muted overflow-hidden">
            {imageUrl ? (
              <img src={imageUrl} alt={product.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">No image</div>
            )}
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">{product.title}</h1>
          <div className="text-muted-foreground mb-4">{product.description}</div>
          {product.shop?.handle && (
            <div className="mb-4 text-sm">
              Sold by: <Link className="underline" to={`/shop/${product.shop.handle}`}>{product.shop.name || `@${product.shop.handle}`}</Link>
            </div>
          )}
          <div className="text-2xl font-bold mb-6">{product.currency} {(product.price_cents/100).toFixed(2)}</div>
          <div className="flex items-center gap-3">
            <Input type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} className="w-24" />
            <Button onClick={() => addMutation.mutate()}>Add to Cart</Button>
          </div>
          <div className="mt-10">
            <h2 className="text-xl font-semibold mb-4">Related products</h2>
            {!related?.length ? (
              <div className="text-muted-foreground text-sm">No related products yet.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {related.map((p: any) => (
                  <Link key={p.id} to={`/products/${p.id}`} className="border rounded-xl p-3 hover:shadow-md transition">
                    <div className="aspect-square rounded-md overflow-hidden bg-muted mb-2">
                      {p.product_images?.[0]?.url ? (
                        <img src={p.product_images[0].url} alt={p.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
                      )}
                    </div>
                    <div className="text-sm font-medium line-clamp-1">{p.title}</div>
                    <div className="text-xs text-muted-foreground">{p.currency} {(p.price_cents/100).toFixed(2)}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};
export default ProductDetail;